// Miroir côté client de `Trades` (app/models/concerns/trades.rb) : normalize_lot_number, lot_number_key, build_custom_trade, catalog_key.

/**
 * Règles pures des lots personnalisés du marché (« + Ajouter un lot personnalisé »).
 *
 * Module sans DOM ni dépendance, pour être EXÉCUTÉ sous `node --test`
 * (`test/javascript/controllers/custom_lot_rules.test.mjs`) et réutilisé par le
 * contrôleur Stimulus du sélecteur de lots.
 *
 * ⚠️ `takenNumberState` ne grise JAMAIS une option COCHÉE : une case désactivée
 * n'est pas soumise, le lot disparaîtrait du marché sans bruit.
 */

export const CUSTOM_LOT_LABEL_MAX = 90;
export const LOT_NUMBER_FORMAT = /^\d{1,3}[a-z]?$/i;
// S1 : apostrophe autorisée. Cc / Cf : NUL, DEL, inversion bidi (U+202E), espace sans chasse,
// BOM… Testé APRÈS compactage des blancs, donc tab / CR / LF deviennent une espace et passent.
export const CUSTOM_LOT_FORBIDDEN_CHARS = /[<>&"\p{Cc}\p{Cf}]/u;
const MARKUP_CHARS = /[<>&"]/;
// Blancs = propriété Unicode White_Space (espace insécable comprise, BOM exclu), comme
// `[[:space:]]` en Ruby. PAS `\s` / `trim` : ils avalent le BOM (Cf) et ignorent NEL (U+0085).
const BLANK_RUN = /\p{White_Space}+/gu;
const BLANK_EDGES = /^\p{White_Space}+|\p{White_Space}+$/gu;

export const MESSAGES = Object.freeze({
  numberFormat:
    'Le numéro de lot doit être composé de chiffres, avec éventuellement une lettre (ex. 01, 10a).',
  labelEmpty: "Indiquez l'intitulé du lot.",
  labelTooLong: `L'intitulé ne peut pas dépasser ${CUSTOM_LOT_LABEL_MAX} caractères.`,
  labelForbidden: 'L\'intitulé ne peut pas contenir < > & ou "',
  labelInvisible: "L'intitulé contient un caractère invisible non autorisé.",
  numberTaken: (number, takenBy) =>
    `Le lot ${number} est déjà sélectionné sur ce marché (${takenBy}).`,
  catalogChecked: "Ce lot fait déjà partie des lots de l'agence : il a été coché.", // S7
  existingChecked: 'Ce lot est déjà sur ce marché : il a été coché.',
  lotAdded: (lot) => `Lot ${lot} ajouté`,
  numberAvailable: (number) => `n° ${number} de nouveau disponible`,
  numberTakenBadge: (number) => `n° ${number} déjà pris`,
});

// Retire les blancs (White_Space) de tête et de fin.
function stripBlanks(text) {
  return text.replace(BLANK_EDGES, '');
}

/**
 * « 1 » → « 01 », « 10A » → « 10a », « 001 » → « 001 » ; sinon null.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeLotNumber(raw) {
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const value = stripBlanks(String(raw));
  if (!LOT_NUMBER_FORMAT.test(value)) return null;
  const lower = value.toLowerCase();
  return /^\d(?:[a-z])?$/.test(lower) ? `0${lower}` : lower;
}

/**
 * Numéro normalisé en tête de « NN - Intitulé » (« 1 - X » et « 01 - Y » → « 01 ») ; sinon null.
 * @param {unknown} value
 * @returns {string|null}
 */
export function lotNumberKey(value) {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d+[a-z]?)\p{White_Space}*-/iu);
  return match ? normalizeLotNumber(match[1]) : null;
}

// Intitulé nettoyé (blancs de bord retirés, blancs internes compactés en une espace) ; '' si absent.
function squish(label) {
  if (typeof label !== 'string' && typeof label !== 'number') return '';
  return stripBlanks(String(label)).replace(BLANK_RUN, ' ');
}

/**
 * Clé de comparaison d'un lot avec la liste de l'agence et les lots déjà présents :
 * « numéro normalisé - intitulé nettoyé en minuscules », l'intitulé étant ce qui suit le
 * PREMIER tiret après le numéro. « 1 - Démolition » et « 01 -  DÉMOLITION » → « 01 - démolition ».
 * Sans numéro : la valeur en minuscules. null si ce n'est pas une chaîne.
 * @param {unknown} value
 * @returns {string|null}
 */
export function catalogKey(value) {
  if (typeof value !== 'string') return null;
  const number = lotNumberKey(value);
  if (number === null) return value.toLowerCase();
  const label = value.slice(value.indexOf('-') + 1);
  return `${number} - ${squish(label).toLowerCase()}`;
}

// Longueur en caractères (points de code), comme `String#length` en Ruby.
function charLength(text) {
  return Array.from(text).length;
}

// Message d'erreur de l'intitulé déjà nettoyé, ou null s'il est valide.
function labelError(label) {
  if (label === '') return MESSAGES.labelEmpty;
  if (CUSTOM_LOT_FORBIDDEN_CHARS.test(label))
    return MARKUP_CHARS.test(label) ? MESSAGES.labelForbidden : MESSAGES.labelInvisible;
  if (charLength(label) > CUSTOM_LOT_LABEL_MAX) return MESSAGES.labelTooLong;
  return null;
}

/**
 * « NN - Intitulé » ou null (numéro invalide, intitulé vide, trop long ou interdit).
 * @param {unknown} number
 * @param {unknown} label
 * @returns {string|null}
 */
export function buildCustomLot(number, label) {
  const normalized = normalizeLotNumber(number);
  const cleaned = squish(label);
  if (normalized === null || labelError(cleaned) !== null) return null;
  return `${normalized} - ${cleaned}`;
}

/**
 * Valide la saisie du lot personnalisé, numéro d'abord.
 *
 * Correspondance par `catalogKey` : un lot déjà présent (liste de l'agence d'abord, puis lot
 * propre au marché, perso ou orphelin) est renvoyé TEL QU'IL EST STOCKÉ, pour être coché au lieu
 * d'ajouter une 2e case au même numéro (que le serveur refuserait). Une case cochée qui est ce
 * même lot n'est pas une collision de numéro.
 *
 * @param {{ number: unknown, label: unknown, checkedValues?: string[], catalogValues?: string[], existingValues?: string[] }} input
 *   catalogValues : lots de l'agence ; existingValues : lots propres au marché (perso ou orphelins)
 * @returns {{ ok: true, value: string } | { ok: true, catalogMatch: string } | { ok: true, existingMatch: string } | { ok: false, field: 'number'|'label', message: string }}
 */
export function validateCustomLot({
  number,
  label,
  checkedValues = [],
  catalogValues = [],
  existingValues = [],
}) {
  const normalized = normalizeLotNumber(number);
  if (normalized === null) return { ok: false, field: 'number', message: MESSAGES.numberFormat };

  const cleaned = squish(label);
  const error = labelError(cleaned);
  if (error !== null) return { ok: false, field: 'label', message: error };

  const lot = `${normalized} - ${cleaned}`;
  const lotKey = catalogKey(lot);
  const takenBy = checkedValues.find(
    (v) => lotNumberKey(v) === normalized && catalogKey(v) !== lotKey
  );
  if (takenBy)
    return { ok: false, field: 'number', message: MESSAGES.numberTaken(normalized, takenBy) };

  const catalogMatch = catalogValues.find((v) => catalogKey(v) === lotKey);
  if (catalogMatch) return { ok: true, catalogMatch };
  const existingMatch = existingValues.find((v) => catalogKey(v) === lotKey);
  return existingMatch ? { ok: true, existingMatch } : { ok: true, value: lot };
}

/**
 * Grise chaque option non cochée dont le numéro est déjà pris par une option cochée.
 *
 * @param {{ value: string, checked: boolean }[]} options
 * @returns {{ value: string, disabled: boolean, takenBy: string|null }[]}
 */
export function takenNumberState(options) {
  const checkedByKey = new Map();
  for (const { value, checked } of options) {
    const key = lotNumberKey(value);
    if (checked && key !== null && !checkedByKey.has(key)) checkedByKey.set(key, value);
  }

  return options.map(({ value, checked }) => {
    const takenBy = checked ? null : (checkedByKey.get(lotNumberKey(value)) ?? null);
    return { value, disabled: takenBy !== null, takenBy };
  });
}

// Numéros normalisés portés par au moins une option grisée, dans l'ordre de première apparition.
function greyedNumbers(state) {
  const numbers = new Set();
  for (const { value, disabled } of state ?? []) {
    const key = disabled ? lotNumberKey(value) : null;
    if (key !== null) numbers.add(key);
  }
  return numbers;
}

/**
 * Numéros qui étaient grisés avant et ne le sont plus (« n° 01 de nouveau disponible »).
 * Rien au premier rendu (`previous` absent).
 *
 * @param {{ value: string, disabled: boolean }[]|null|undefined} previous sortie précédente de `takenNumberState`
 * @param {{ value: string, disabled: boolean }[]} next sortie courante de `takenNumberState`
 * @returns {string[]}
 */
export function freedLotNumbers(previous, next) {
  if (!previous) return [];
  const stillGreyed = greyedNumbers(next);
  return [...greyedNumbers(previous)].filter((number) => !stillGreyed.has(number));
}

/**
 * Id de la prochaine case perso : `${prefix}_custom_${n}`, n partant de `start` et sautant
 * les ids déjà présents. Jamais dérivé de l'intitulé.
 *
 * @param {string} prefix
 * @param {number} start
 * @param {(id: string) => boolean} isTaken
 * @returns {string}
 */
export function nextCustomLotId(prefix, start, isTaken) {
  let index = Number.isInteger(start) && start > 0 ? start : 0;
  while (isTaken(`${prefix}_custom_${index}`)) index += 1;
  return `${prefix}_custom_${index}`;
}
